export type AssetDeliveryConfig = {
  /**
   * Cloudflare Images delivery origin. This can be either the bare delivery
   * URL (for example, https://imagedelivery.net) or a URL that already ends
   * with the Images account hash.
   */
  deliveryUrl: string;
  accountHash?: string;
  /** Cloudflare Images variant name. */
  variant?: string;
  /** Prefix used for Cloudflare Images custom IDs. */
  assetPrefix?: string;
};

type NormalizedAssetDeliveryConfig = {
  deliveryBaseUrl: string;
  variant: string;
  assetPrefix: string;
};

const DEFAULT_VARIANT = "public";
const DEFAULT_ASSET_PREFIX = "rujira-ui";
const DEFAULT_DELIVERY_URL = "https://imagedelivery.net/3hJbJ0lHr_OgK8uf4TpGSg";

const DEFAULT_CONFIG: AssetDeliveryConfig = {
  deliveryUrl: DEFAULT_DELIVERY_URL,
  variant: DEFAULT_VARIANT,
  assetPrefix: DEFAULT_ASSET_PREFIX,
};

const stripTemplateSuffix = (url: string) =>
  url.replace(/\/(?:<|\{)?(?:image[_-]?id|image_id)(?:>|\})?(?:\/.*)?$/i, "");

const normalizeDeliveryBaseUrl = (
  deliveryUrl: string,
  accountHash?: string
) => {
  const hash = accountHash?.trim();
  let base = deliveryUrl.trim();

  if (!base) throw new Error("Asset delivery URL cannot be empty");

  if (hash) {
    base = base.replace(
      /(?:<|\{)(?:account[_-]?hash|account_hash)(?:>|\})/gi,
      hash
    );
  }

  base = stripTemplateSuffix(base).replace(/\/+$/, "");

  if (hash) {
    const lastSegment = base.split("/").at(-1);
    if (lastSegment !== hash) base = `${base}/${encodeURIComponent(hash)}`;
  }

  return base;
};

const normalizePath = (value: string, label: string) => {
  const normalized = value.trim().replace(/^\/+|\/+$/g, "");
  if (!normalized) throw new Error(`${label} cannot be empty`);
  if (normalized.split("/").some((segment) => segment === "..")) {
    throw new Error(`${label} cannot contain parent path segments`);
  }
  return normalized;
};

const encodePath = (value: string) =>
  value
    .split("/")
    .map((segment) => encodeURIComponent(segment))
    .join("/");

const normalizeConfig = (
  config: AssetDeliveryConfig
): NormalizedAssetDeliveryConfig => ({
  deliveryBaseUrl: normalizeDeliveryBaseUrl(
    config.deliveryUrl,
    config.accountHash
  ),
  variant: normalizePath(config.variant || DEFAULT_VARIANT, "Asset variant"),
  assetPrefix: normalizePath(
    config.assetPrefix || DEFAULT_ASSET_PREFIX,
    "Asset prefix"
  ),
});

let deliveryConfig = normalizeConfig(DEFAULT_CONFIG);

/**
 * Optionally override the built-in Rujira Cloudflare Images configuration.
 * Passing null restores the library defaults.
 */
export const configureAssetDelivery = (config: AssetDeliveryConfig | null) => {
  deliveryConfig = normalizeConfig(config || DEFAULT_CONFIG);
};

/** Resolve a path relative to src/assets to its Cloudflare Images URL. */
export const getAssetDeliveryUrl = (assetPath: string) => {
  const path = normalizePath(assetPath, "Asset path");
  const imageId = `${deliveryConfig.assetPrefix}/${path}`;
  return `${deliveryConfig.deliveryBaseUrl}/${encodePath(imageId)}/${encodeURIComponent(
    deliveryConfig.variant
  )}`;
};
